import { isExited, clientStatusLabel } from '@/utils/statusTransitions';
import { ReportButton } from '@/components/reports/ReportButton';
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useClients } from '@/hooks/useClients';
import { useSites } from '@/hooks/useSites';
import FamilyMemberRow from './FamilyMemberRow';
import { isWithinCurrentOperationalDay } from '@/utils/operationalDay';

interface SiteDailyCensusProps {
  siteId: string;
  siteName: string;
  onBack: () => void;
  onSelectClient?: (clientId: string) => void;
}

const SiteDailyCensus: React.FC<SiteDailyCensusProps> = ({ siteId, siteName, onBack, onSelectClient }) => {
  const { userRole } = useAuth();
  const { clients: dbClients = [] } = useClients();
  const { sites: dbSites = [] } = useSites();
  const isSuperAdmin = userRole === 'super_admin';

  // Get clients for this site - always use real database data.
  // Unbedded clients only stay on the census during the operational day they were
  // unbedded (rolls over at 2 AM); after that they remain in the Clients list but
  // drop off the census.
  const clients = dbClients.filter((c: any) =>
    (c.site_id === siteId || c.siteId === siteId)
  ).filter((c: any) => {
    if (isExited(c)) return false;
    // Census lists only clients holding a bed, or unbedded during the current
    // operational day (rolls over at 2 AM).
    if (c.status === 'unbedded') return isWithinCurrentOperationalDay(c.unbedded_at ?? c.unbeddedAt);
    return !!(c.room_id ?? c.roomId);
  }).map(c => ({
    ...c,
    room: c.room || 'Unassigned'
  }));
  
  // Get site info from database
  const site = dbSites.find(s => s.id === siteId);
  const isFamilyShelter = site?.is_family_shelter || false;
  
  const groupClientsByRoom = () => {
    const grouped = new Map<string, typeof clients>();
    
    clients.forEach(client => {
      const roomKey = client.room || 'Unassigned';
      if (!grouped.has(roomKey)) {
        grouped.set(roomKey, []);
      }
      grouped.get(roomKey)!.push(client);
    });
    
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));
  };

  const roomGroups = groupClientsByRoom();
  const checkedInCount = clients.filter(c => c.status === 'checked-in').length;

  // Compute pass count
  const now = new Date();
  const passCount = clients.filter((c: any) => {
    if (c.passStart && c.passEnd) return now >= new Date(c.passStart) && now <= new Date(c.passEnd);
    if (c.pass_start && c.pass_end) return now >= new Date(c.pass_start) && now <= new Date(c.pass_end);
    return false;
  }).length;

  // Calculate total family members for family shelters
  const totalFamilyMembers = isFamilyShelter ? clients.reduce((acc, client) => {
    const clientWithFamily = client as typeof client & { partnerName?: string; children?: { name: string; age?: number }[] };
    const partnerCount = clientWithFamily.partnerName ? 1 : 0;
    const childrenCount = clientWithFamily.children?.length || 0;
    return acc + 1 + partnerCount + childrenCount;
  }, 0) : clients.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Facilities
          </Button>
        </div>
        
        <ReportButton
          title={`Daily Census — ${siteName}`}
          rows={clients as any[]}
          columns={[
            { key: 'name', label: 'Name', value: (c: any) => c.name || '' },
            { key: 'room', label: 'Room', value: (c: any) => c.room || 'Unassigned' },
            { key: 'status', label: 'Status', value: (c: any) => clientStatusLabel(c) },
            { key: 'caseManager', label: 'Case Manager', value: (c: any) => c.caseWorker || '—', defaultOn: false },
          ]}
          summary={(rows) => ({
            [isFamilyShelter ? 'Families In' : 'Checked In']: rows.filter((c: any) => c.status === 'checked-in').length,
            [isFamilyShelter ? 'Total People' : 'Total Clients']: isFamilyShelter ? totalFamilyMembers : rows.length,
            'On Pass': passCount,
          })}
        />
      </div>

      {/* Centered Title - matching Staff Dashboard */}
      <div className="text-center space-y-3">
        <h1 className="text-5xl font-bold text-gray-800 dark:text-gray-100 tracking-tight font-['Montserrat',sans-serif]">Daily Census</h1>
        <div className="flex items-center justify-center gap-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-2xl font-normal text-gray-500 dark:text-gray-400">{siteName}</h2>
          {isFamilyShelter && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">
              Family Shelter
            </span>
          )}
        </div>
        <p className="text-lg text-muted-foreground">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </p>
      </div>

      {/* Quick Stats - matching Staff Dashboard format */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[hsl(var(--status-checked-in))]">{checkedInCount}</p>
              <p className="text-base text-muted-foreground">
                {isFamilyShelter ? 'Families In' : 'Checked In'}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[hsl(var(--status-checked-out))]">{clients.length - checkedInCount}</p>
              <p className="text-base text-muted-foreground">
                {isFamilyShelter ? 'Families Out' : 'Checked Out'}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-gators-orange">
                {isFamilyShelter ? totalFamilyMembers : clients.length}
              </p>
              <p className="text-base text-muted-foreground">
                {isFamilyShelter ? 'Total People' : 'Total Clients'}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
               <p className="text-3xl font-bold text-[hsl(var(--status-pass))]">{passCount}</p>
              <p className="text-base text-muted-foreground">Pass</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client List Table - matching Staff Dashboard format */}
      {(
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Space for search if needed */}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium w-[35%]">
                      {isFamilyShelter ? 'Family / Member' : 'Name'}
                    </th>
                    <th className="text-left p-4 font-medium w-[12%]">Room</th>
                    <th className="text-left p-4 font-medium w-[10%]">Age</th>
                    <th className="text-left p-4 font-medium w-[20%]">Case Manager</th>
                    <th className="text-left p-4 font-medium w-[23%]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(client => (
                    <FamilyMemberRow
                      key={client.id}
                      client={client}
                      siteId={siteId}
                      onSelectClient={onSelectClient}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SiteDailyCensus;
